import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "./Blog.scss";
import PageBanner from "../layouts/PageBanner";
import aboutHero from "../../assets/about-hero.jpg";
import DOMPurify from "dompurify";

const API = "https://api.ainfotech.com.np";

export default function Blogcontent() {
  const { slug } = useParams(); // route is /blog/:slug
  const [post, setPost] = useState(null);
  const [recent, setRecent] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        setErr("");
        // fetch details
        const res = await fetch(`${API}/api/blog/${slug}`);
        if (res.status === 404) { setPost(null); return; }
        const data = await res.json();
        if (!res.ok) throw new Error(data?.error || "Failed to load");
        setPost(data);

        // fetch recent (grab a few extra so we still have ~5 after filtering)
        const r = await fetch(`${API}/api/blog?status=published&page=1&limit=8`);
        const rd = await r.json();
        setRecent(rd.items || []);

        window.scrollTo(0, 0);
      } catch (e) {
        setErr(e.message);
      }
    })();
  }, [slug]);

  // Sanitize main content
  const safeHtml = useMemo(
    () => ({ __html: DOMPurify.sanitize(post?.content_html || "") }),
    [post]
  );

  // Exclude the current post (by id or slug) from "Recent Posts"
  const recentFiltered = useMemo(() => {
    if (!post) return (recent || []).slice(0, 5);
    return (recent || [])
      .filter((p) => (post.id ? p.id !== post.id : p.slug !== post.slug))
      .slice(0, 5);
  }, [recent, post]);

  if (!post) {
    return (
      <div className="container py-5">
        <h3 className="mb-2">Post not found</h3>
        <p className="text-muted">The article you’re looking for may have been moved or renamed.</p>
        <Link to="/blog" className="btn btn-outline-secondary">Back to Blog</Link>
      </div>
    );
  }

  const hero = post.cover_image ? `${API}${post.cover_image}` : undefined;
  const date = post.published_at ? new Date(post.published_at).toLocaleDateString() : "";

  return (
    <div className="avena-blog">
      <PageBanner
        title="Blog"
        bg={aboutHero}
        crumbs={[{ label: "Home", href: "/" }, { label: "Blog" }]}
      />

      <section className="container py-5">
        {err && <div className="alert alert-danger">{err}</div>}

        <div className="row g-5">
          {/* MAIN */}
          <div className="col-12 col-lg-8">
            <article className="avena-post">
              <header className="avena-post__meta">
                <h1 className="avena-post__title">{post.title}</h1>
                <div className="avena-post__byline">
                  <div className="meta-info">
                    {date && (
                      <span className="meta-date">
                        <i className="fa-regular fa-calendar"></i> {date}
                      </span>
                    )}

                    {post.tag_primary && (
                      <span className="meta-tag">
                        <i className="fa-solid fa-tag"></i> {post.tag_primary}
                      </span>
                    )}

                    {post.tag_secondary && (
                      <span className="meta-tag">
                        <i className="fa-solid fa-tag"></i> {post.tag_secondary}
                      </span>
                    )}
                  </div>
                </div>
              </header>

              {hero && (
                <figure className="avena-post__hero">
                  <img src={hero} alt={post.title} />
                </figure>
              )}

              <div className="pd-rich" dangerouslySetInnerHTML={safeHtml} />
            </article>
          </div>

          {/* SIDEBAR */}
          <div className="col-12 col-lg-4">
            <aside className="card p-3 avena-sidebar">
              <h5 className="avena-sidebar__title">Recent Posts</h5>
              <ul className="list-unstyled avena-sidebar__list">
                {recentFiltered.map((p) => (
                  <li key={p.id} className="avena-sidebar__item">
                    <Link to={`/blog/${p.slug}`} className="avena-sidebar__link">
                      {p.cover_image ? (
                        <img
                          src={`${API}${p.cover_image}`}
                          alt={p.title}
                          className="rounded"
                          style={{ width: "100px", height: "100px", objectFit: "cover" }}
                        />
                      ) : null}
                      <div>
                        <div className="avena-sidebar__title">{p.title}</div>
                        {p.published_at && (
                          <small className="text-muted px-2">
                            {new Date(p.published_at).toLocaleDateString()}
                          </small>
                        )}
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>

              <div className="mt-3">
                <Link to="/blog" className="btn btn-sm btn-outline-secondary">View all</Link>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
}
